import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Space,
  message,
  Tag,
  Typography,
  Tabs,
} from 'antd';
import {
  PlayCircleOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  YoutubeOutlined,
  GoogleOutlined,
  CloudDownloadOutlined,
} from '@ant-design/icons';
import api from '../../services/api';

const { Title } = Typography;

const SchedulerManagement = ({ embedded = false }) => {
  const [tasks, setTasks] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState('tasks');

  useEffect(() => {
    fetchTasks();
    fetchHistory();
  }, []);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/tasks');
      setTasks(response.data.data || []);
    } catch (error) {
      message.error('Failed to load tasks');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await api.get('/api/tasks/history?limit=50');
      setHistory(response.data.data || []);
    } catch (error) {
      console.error('Failed to load history:', error);
    }
  };

  const handleTrigger = async (taskId) => {
    try {
      await api.post('/api/tasks/trigger', { task_id: taskId });
      message.success('Task triggered successfully');
      fetchTasks();
      fetchHistory();
    } catch (error) {
      message.error('Failed to trigger task');
      console.error(error);
    }
  };

  const handleUpdateSchedule = async (values) => {
    try {
      await api.put('/api/tasks/schedule', {
        task_id: selectedTask.id,
        ...values,
      });
      message.success('Schedule updated successfully');
      setModalVisible(false);
      fetchTasks();
    } catch (error) {
      message.error('Failed to update schedule');
      console.error(error);
    }
  };

  const taskColumns = [
    {
      title: 'Task Info',
      key: 'task_info',
      render: (_, record) => (
        <div style={{ minWidth: 300 }}>
          {/* Task name and type */}
          <div style={{ display: 'flex', alignItems: 'start', gap: 8, marginBottom: 6 }}>
            <ClockCircleOutlined style={{ color: '#14B8A6', fontSize: '16px', marginTop: 2 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 500, fontSize: '14px', lineHeight: '1.4', marginBottom: 4 }}>
                {record.name}
              </div>
              <Tag color="blue" style={{ marginBottom: 4 }}>{record.task_type}</Tag>
            </div>
          </div>

          {/* Schedule info */}
          <div style={{ marginBottom: 6, fontSize: '12px', color: '#666' }}>
            {record.schedule_type === 'CRON' && (
              <Tag icon={<ClockCircleOutlined />} color="default">{record.cron_expression}</Tag>
            )}
            {record.schedule_type === 'INTERVAL' && (
              <Tag icon={<SyncOutlined />} color="default">Every {record.interval_seconds}s</Tag>
            )}
            {!record.schedule_type && <Tag color="default">{record.schedule_type}</Tag>}
          </div>

          {/* Status */}
          <div>
            {!record.is_active ? (
              <Tag color="default">Inactive</Tag>
            ) : record.is_running ? (
              <Tag icon={<SyncOutlined spin />} color="processing">Running</Tag>
            ) : (
              <Tag color="success">Active</Tag>
            )}
            {record.last_run_status && (
              <Tag
                color={
                  record.last_run_status === 'SUCCESS'
                    ? 'success'
                    : record.last_run_status === 'FAILED'
                    ? 'error'
                    : 'default'
                }
                style={{ marginLeft: 4 }}
              >
                Last: {record.last_run_status}
              </Tag>
            )}
          </div>
        </div>
      ),
      width: '40%',
    },
    {
      title: 'Run Times',
      key: 'run_times',
      render: (_, record) => (
        <div style={{ minWidth: 160 }}>
          <div style={{ fontSize: '12px', marginBottom: 8 }}>
            <div style={{ color: '#666', marginBottom: 2 }}>Last Run</div>
            <div style={{ fontWeight: 500 }}>
              {record.last_run_at ? new Date(record.last_run_at).toLocaleString('en-GB', {
                day: '2-digit',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit'
              }) : 'Never'}
            </div>
          </div>
          <div style={{ fontSize: '12px', paddingTop: 8, borderTop: '1px dashed #e8e8e8' }}>
            <div style={{ color: '#666', marginBottom: 2 }}>Next Run</div>
            <div style={{ fontWeight: 500 }}>
              {record.next_run ? new Date(record.next_run).toLocaleString('en-GB', {
                day: '2-digit',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit'
              }) : '-'}
            </div>
          </div>
        </div>
      ),
      width: '200px',
    },
    {
      title: 'Aksi',
      key: 'actions',
      fixed: 'right',
      width: 110,
      render: (_, record) => (
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          <Button
            type="primary"
            icon={<PlayCircleOutlined />}
            size="small"
            onClick={() => handleTrigger(record.id)}
            disabled={record.is_running}
            block
          >
            Trigger
          </Button>
          <Button
            icon={<ClockCircleOutlined />}
            size="small"
            onClick={() => {
              setSelectedTask(record);
              form.setFieldsValue({
                schedule_type: record.schedule_type,
                cron_expression: record.cron_expression,
                interval_seconds: record.interval_seconds,
                is_active: record.is_active,
              });
              setModalVisible(true);
            }}
            block
          >
            Edit Schedule
          </Button>
        </Space>
      ),
    },
  ];

  const historyColumns = [
    {
      title: 'Task Info',
      key: 'task_info',
      render: (_, record) => {
        const statusConfig = {
          SUCCESS: { color: 'success', icon: <CheckCircleOutlined /> },
          FAILED: { color: 'error', icon: <CloseCircleOutlined /> },
          RUNNING: { color: 'processing', icon: <SyncOutlined spin /> },
          PENDING: { color: 'default', icon: <ClockCircleOutlined /> },
        };
        const { color, icon } = statusConfig[record.status] || statusConfig.PENDING;

        return (
          <div style={{ minWidth: 280 }}>
            {/* Task name */}
            <div style={{ fontWeight: 500, fontSize: '14px', marginBottom: 6 }}>
              {record.task_name}
            </div>

            {/* Status and trigger */}
            <div>
              <Tag icon={icon} color={color} style={{ marginRight: 4 }}>
                {record.status}
              </Tag>
              {record.triggered_by && (
                <Tag color="default">{record.triggered_by}</Tag>
              )}
            </div>
          </div>
        );
      },
      width: '35%',
    },
    {
      title: 'Execution Time',
      key: 'execution_time',
      render: (_, record) => (
        <div style={{ minWidth: 180 }}>
          <div style={{ fontSize: '12px', marginBottom: 8 }}>
            <div style={{ color: '#666', marginBottom: 2 }}>Started</div>
            <div style={{ fontWeight: 500 }}>
              {record.started_at ? new Date(record.started_at).toLocaleString('en-GB', {
                day: '2-digit',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              }) : '-'}
            </div>
          </div>
          <div style={{ fontSize: '12px' }}>
            <div style={{ color: '#666', marginBottom: 2 }}>Duration</div>
            <div style={{ fontWeight: 500 }}>
              {record.duration_seconds ? `${record.duration_seconds.toFixed(2)}s` : '-'}
            </div>
          </div>
        </div>
      ),
      width: '220px',
    },
  ];

  const handleQuickTrigger = async (taskNameOrType) => {
    try {
      // Try to find task by name first, then by type
      const task = tasks.find(
        t => t.name === taskNameOrType || t.task_type === taskNameOrType
      );

      if (task) {
        await handleTrigger(task.id);
      } else {
        message.error(`Task "${taskNameOrType}" not found. Available tasks: ${tasks.map(t => t.name).join(', ')}`);
      }
    } catch (error) {
      message.error('Failed to trigger task');
      console.error(error);
    }
  };

  const handleRefreshAll = () => {
    fetchTasks();
    fetchHistory();
    message.success('Data refreshed');
  };

  const content = (
    <>
      {!embedded && <Title level={3} style={{ margin: '0 0 20px 0' }}>Scheduler Management</Title>}

      {/* Quick Actions */}
      <Card
        size="small"
        title="Quick Actions"
        style={{ marginBottom: 20 }}
        extra={
          <Button
            icon={<SyncOutlined />}
            onClick={handleRefreshAll}
            size="small"
          >
            Refresh
          </Button>
        }
      >
        <Space wrap>
          <Button
            type="primary"
            icon={<CloudDownloadOutlined />}
            onClick={() => handleQuickTrigger('api_data_pull')}
            style={{ background: '#14B8A6', borderColor: '#14B8A6' }}
          >
            Pull API Data
          </Button>
          <Button
            type="primary"
            icon={<YoutubeOutlined />}
            onClick={() => handleQuickTrigger('scraping_youtube')}
          >
            Trigger YouTube Scraper
          </Button>
          <Button
            type="primary"
            icon={<GoogleOutlined />}
            onClick={() => handleQuickTrigger('scraping_google')}
          >
            Trigger Google Scraper
          </Button>
          <Button
            type="primary"
            icon={<SyncOutlined />}
            onClick={() => handleQuickTrigger('scraping_all')}
          >
            Trigger All Scrapers
          </Button>
          <Button
            icon={<PlayCircleOutlined />}
            onClick={() => {
              Modal.confirm({
                title: 'Trigger All Active Tasks',
                content: 'Are you sure you want to trigger all active tasks?',
                onOk: async () => {
                  const activeTasks = tasks.filter(t => t.is_active && !t.is_running);
                  for (const task of activeTasks) {
                    await handleTrigger(task.id);
                  }
                  message.success(`Triggered ${activeTasks.length} tasks`);
                },
              });
            }}
          >
            Trigger All Active
          </Button>
        </Space>
      </Card>

      <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'tasks',
              label: 'Scheduled Tasks',
              children: (
                <Table
                  columns={taskColumns}
                  dataSource={tasks}
                  loading={loading}
                  rowKey="id"
                  pagination={{ pageSize: 10 }}
                />
              ),
            },
            {
              key: 'history',
              label: 'Execution History',
              children: (
                <Table
                  columns={historyColumns}
                  dataSource={history}
                  rowKey="id"
                  pagination={{ pageSize: 20 }}
                />
              ),
            },
          ]}
        />

        <Modal
          title="Edit Schedule"
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          onOk={() => form.submit()}
          width={600}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleUpdateSchedule}
          >
            <Form.Item
              name="schedule_type"
              label="Schedule Type"
            >
              <Select>
                <Select.Option value="CRON">Cron Expression</Select.Option>
                <Select.Option value="INTERVAL">Fixed Interval</Select.Option>
                <Select.Option value="MANUAL">Manual Only</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.schedule_type !== currentValues.schedule_type
              }
            >
              {({ getFieldValue }) => {
                const scheduleType = getFieldValue('schedule_type');
                if (scheduleType === 'CRON') {
                  return (
                    <Form.Item
                      name="cron_expression"
                      label="Cron Expression"
                      help="Example: 0 2 * * * (daily at 2:00 AM)"
                    >
                      <Input placeholder="0 2 * * *" />
                    </Form.Item>
                  );
                } else if (scheduleType === 'INTERVAL') {
                  return (
                    <Form.Item
                      name="interval_seconds"
                      label="Interval (seconds)"
                    >
                      <Input type="number" placeholder="3600" />
                    </Form.Item>
                  );
                }
                return null;
              }}
            </Form.Item>

            <Form.Item
              name="is_active"
              label="Active"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Form>
        </Modal>
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <Card>{content}</Card>
  );
};

export default SchedulerManagement;
